import { serverTimestamp, type Timestamp } from "firebase/firestore";
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import { TKey } from "../../shared/translations/translationKeys";
import { type PlaniraLogData, PlaniraLogType } from "../../shared/types";
import { PlanTemplateType } from "../../templates/generated/template_types";
import type { TemplateComplete } from "../../templates/generator/types";
import {
  ModuleDoneType,
  ModuleState,
  ModuleType,
  PlanQuestionType,
  PlanType,
} from "../../templates/types";
import { ActivityEventType } from "../activities/types";
import type { HotelRawData } from "../affiliates/booking/types-hotel";
import type {
  BusinessPlanModel,
  BusinessStaticUserInfoWithUserUid,
} from "../business/plan/BusinessPlanModel";
import type { Api } from "../core/apis";
import { BaseModel, type BaseModelData } from "../core/BaseModel";
import { showConfetti } from "../core/states.ts/showConfetti";
import type { Stores } from "../core/stores";
import { trackEvent } from "../core/trackEvent";
import type BaseModuleModel from "../modules/core/BaseModuleModel";
import type {
  ModuleModelData,
  Snapshot,
} from "../modules/core/BaseModuleModel";
import type PlanInfoModuleModel from "../modules/PlanInfoModule/PlanInfoModuleModel";
import type SectionModel from "../sections/SectionModel";
import type { SectionModelData } from "../sections/SectionModel";
import { log } from "../ui/log";
import type UserModel from "../users/UserModel";
import type { UserModelData } from "../users/UserModel";
import { type DateFormat, StartOfWeek } from "./types";

export type ArchiveData = {
  moduleIds: string[];
  sectionIds: string[];
  userIds: string[];
};

export type PlanModelSettings = {
  isLocked: boolean;
  dateFormat: DateFormat;
  startOfWeek: StartOfWeek;
  useSections: boolean;
};

export enum PlanModelStep {
  hasUsersWithMultiplePlans = "hasUsersWithMultiplePlans",
  hasMultipleUsers = "hasMultipleUsers",
  hasMoreThanFiveUsers = "hasMoreThanFiveUsers",
  hasDescription = "hasDescription",
  hasSetCardToDone = "hasSetCardToDone",
  hasClickedOnShare = "hasClickedOnShare",
  hasComments = "hasComments",
  hasAddedCard = "hasAddedCard",
}

export type PlanModelProgress = {
  // Bumped when actioned on a step
  rating: number;

  // Steps
  [PlanModelStep.hasUsersWithMultiplePlans]?: number;
  [PlanModelStep.hasMultipleUsers]?: number;
  [PlanModelStep.hasMoreThanFiveUsers]?: number;
  [PlanModelStep.hasDescription]?: number;
  [PlanModelStep.hasSetCardToDone]?: number;
  [PlanModelStep.hasClickedOnShare]?: number;
  [PlanModelStep.hasComments]?: number;
  [PlanModelStep.hasAddedCard]?: number;
};

export type PlanTag = {
  uid: string;
  color: string;
  textColor: string;
  label: string;
  archived: boolean;
};

export type PlanTagWithOptions = PlanTag & {
  count?: number;
  completed?: number;
  dimmed?: boolean;
};

export type PlanDevInfo = {
  skipped: boolean;
  starred: boolean;
};

export const defaultPlanSettings: PlanModelSettings = {
  isLocked: false,
  dateFormat: "YYYY-MM-DD HH:mm",
  startOfWeek: StartOfWeek.Monday,
  useSections: false,
};

export type AffiliatesHotelsCache = {
  createdAt: number;
  hash: string;
  data: HotelRawData[];
};

export type PlanCache = {
  affiliateHotelsDump?: AffiliatesHotelsCache;
};

// If set, this is a gate when joining
type UserJoinOptions = {
  byCode?: string;
  needToApply?: boolean;
  waitingForApproval?: string[]; // UserUids..
};

export interface PlanModelData extends BaseModelData {
  type: PlanType;
  businessPlanUid: string | null;
  template: PlanTemplateType;
  title: string;
  userIds: string[];
  ghostUsers: string[];
  sectionIds: string[];
  archive: ArchiveData;
  settings: PlanModelSettings;
  progress: PlanModelProgress;
  dev: PlanDevInfo;
  tags: PlanTag[];
  cache: PlanCache;

  joinOptions: UserJoinOptions;
  onTemplateComplete: TemplateComplete | null;
}

export enum AccountSheetActive {
  None = "none",
  Login = "login",
  Register = "register",
  ForgotPassword = "forgot_password",
  SetPassword = "set_password",
}

export default class PlanModel extends BaseModel<PlanModelData> {
  @observable showComments = true;
  @observable showShare = { show: false, showMembers: true };
  @observable showAccountSheet: AccountSheetActive = AccountSheetActive.None;
  @observable showAddStepSheet = false;

  constructor(stores: Stores, api: Api, data: PlanModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      type: data.type ?? PlanType.Planira,
      template: data.template ?? PlanTemplateType.None,
      title: data.title ?? "",
      ghostUsers: data.ghostUsers ?? [],
      settings: data.settings ?? defaultPlanSettings,
      progress: data.progress ?? { rating: 0 },
      dev: data.dev ?? { skipped: false, starred: false },
      tags: data.tags ?? [],
      businessPlanUid: data.businessPlanUid ?? null,
      cache: data.cache ?? {},
      joinOptions: data.joinOptions ?? {},
      onTemplateComplete: data.onTemplateComplete ?? null,
    });

    /*
        if (!this.data.joinOptions.needToApply) {
          this.data.joinOptions.needToApply = true; // TODO REMOVE
        }
         */

    makeObservable(this);
  }

  @computed get availableTags() {
    return this.data.tags.filter((tag) => !tag.archived);
  }

  getTag(uid: string): PlanTag | undefined {
    return this.data.tags.find((tag) => tag.uid === uid);
  }

  @action
  async addTag(
    label: string,
    color: string,
    textColor: string,
  ): Promise<PlanTag | undefined> {
    const exists = this.data.tags.find(
      (tag) => tag.label.toLowerCase() === label.toLowerCase(),
    );

    if (exists && !exists.archived) {
      console.error("Tag exists already", exists);
      return;
    }

    if (exists?.archived) {
      // unarchive instead of adding
      await this.updateTag({ ...exists, archived: false });
      trackEvent("tag_added_existing");

      return exists;
    }

    const newTag = {
      uid: `${Date.now()}`,
      label,
      color,
      textColor,
      archived: false,
    };

    this.data.tags.push(newTag);
    await this.api.plansApi.update(this.uid, { tags: this.data.tags });

    trackEvent("tag_added");
    return newTag;
  }

  @action
  async updateTag(tag: PlanTag) {
    const existingTagIndex = this.data.tags.findIndex(
      (tagE) => tagE.uid === tag.uid,
    );
    if (existingTagIndex === -1) {
      console.error("Tag don't exist to edit", tag);
      return;
    }

    this.data.tags[existingTagIndex] = tag;

    await this.api.plansApi.update(this.uid, { tags: this.data.tags });
  }

  @action
  async updateCache(cache: Partial<PlanCache>) {
    this.data.cache = { ...this.data.cache, ...cache };
    await this.api.plansApi.update(this.uid, { cache: this.data.cache });
  }

  @action
  async trackProgress(step: PlanModelStep, savePlan = false) {
    if (!this.data.progress[step]) {
      this.data.progress[step] = Date.now();
      this.data.progress.rating++;

      if (savePlan) {
        await this.api.plansApi.update(this.uid, {
          progress: this.data.progress,
        });
      }

      trackEvent(`progress_${step}`);
    }
  }

  @action
  setShowComments(value: boolean) {
    this.showComments = value;
  }

  @action
  setShowShare(value: boolean, showMembers = true) {
    this.showShare = { show: value, showMembers };
  }

  @action
  setShowAccountSheet(value: AccountSheetActive) {
    this.showAccountSheet = value;
  }

  @action
  setShowAddStepSheet(value: boolean) {
    this.showAddStepSheet = value;
  }

  @computed get quickStartSectionId() {
    return this.data.sectionIds[1];
  }

  @computed get planInfoSectionId() {
    return this.data.sectionIds[0];
  }

  @computed get creatorUserUid() {
    return this.data.userIds[0]; // Let the first user be the creator always
  }

  @computed get isCreator(): boolean {
    return this.creatorUserUid === this.userInPlan?.uid;
  }

  @computed get isUsersLoaded() {
    return this.data.userIds.length === this.users.length;
  }

  @computed get hasNewActivity() {
    return (
      this.planHasAllDataLoaded && !!this.modules.find((m) => m.hasNewActivity)
    );
  }

  @computed get isSectionsLoaded() {
    return this.data.sectionIds.length === this.sections.length;
  }

  @computed get planHasAllDataLoaded(): boolean {
    return (
      this.isUsersLoaded &&
      this.isSectionsLoaded &&
      this.sections.every((section) => section.isModulesLoaded)
    );
  }

  @computed get hasLocationModule(): boolean {
    return !!this.modules.find((m) => m.data.type === ModuleType.Location);
  }

  @computed get isOnlyOneModule() {
    if (this.sections[1]?.modules.length > 1) {
      return undefined;
    }

    if (this.firstModuleUidNotDone) {
      return this.stores.modulesStore.byId.get(this.firstModuleUidNotDone);
    }

    return undefined;
  }

  @computed get modulesWithQuestions() {
    const _modules = this.modules;

    return this.modules.filter(
      (m) => m.data.questionType !== PlanQuestionType.None,
    );
  }

  @computed get isQuickstart() {
    return this.data.type === PlanType.Quickstart;
  }

  async addPlaniraLogItem(type: PlaniraLogType, text?: string) {
    if (this.userInPlan) {
      const data: PlaniraLogData = {
        createdAt: serverTimestamp() as Timestamp,
        refUserUid: this.userInPlan.uid,
        refPlanUid: this.uid,
        type,
        text,
      };

      log.dev("Added PlaniraLog", data);
      await this.api.planiraLogApi.add(data);
    }
  }

  async addSection({
    isDraggable,
    isExpandable,
    title,
    attractState,
  }: Partial<SectionModelData>) {
    const sectionData: Omit<SectionModelData, "uid"> = {
      ts: serverTimestamp() as Timestamp,
      planUid: this.data.uid,
      moduleIds: [],
      isDraggable,
      isExpandable,
      title: title ?? "",
    };

    if (attractState) {
      sectionData.attractState = attractState;
    }

    const section = await this.api.sectionsApi.add(sectionData);

    log.dev("got new section", section);

    this.data.sectionIds.push(section.uid);
    await this.api.plansApi.set(this);

    return section;
  }

  @action
  async setTitle(title: string) {
    if (!!title && title !== this.data.title) {
      this.data.title = title;
      await this.api.plansApi.update(this.data.uid, {
        title: this.data.title,
      });

      if (this.businessPlan) {
        this.businessPlan.updateTeamTitleFromPlan(this.uid, this.data.title);
      }
    }
  }

  @action
  async toggleLock() {
    this.data.settings.isLocked = !this.data.settings.isLocked;
    await this.api.plansApi.update(this.data.uid, {
      settings: this.data.settings,
    });
  }

  @action
  async setStartOfWeek(day: StartOfWeek) {
    this.data.settings.startOfWeek = day;
    await this.api.plansApi.update(this.data.uid, {
      settings: this.data.settings,
    });
  }

  @action
  async setDateFormat(format: DateFormat) {
    this.data.settings.dateFormat = format;
    await this.api.plansApi.update(this.data.uid, {
      settings: this.data.settings,
    });
  }

  @action
  async setUsesSections(val: boolean) {
    this.data.settings.useSections = val;
    await this.api.plansApi.update(this.data.uid, {
      settings: this.data.settings,
    });
  }

  @action
  async addExistingSection(sectionUid: string) {
    this.data.sectionIds.push(sectionUid);
    await this.api.plansApi.update(this.data.uid, {
      sectionIds: this.data.sectionIds,
    });
  }

  @action
  async removeSection(sectionUid: string) {
    this.data.sectionIds = this.data.sectionIds.filter(
      (sUid) => sUid !== sectionUid,
    );
    await this.api.plansApi.update(this.uid, {
      sectionIds: this.data.sectionIds,
    });
  }

  @action
  async joinWithCurrentUser(
    displayName: string,
    isGhost = false,
    moduleUidsSeen: string[] = [],
  ) {
    if (!isGhost && this.data.settings.isLocked) {
      throw Error("Can not join Plan, it is locked.");
    }

    // Set defaults from superUser
    const { avatarUrl, email } = this.stores.sessionStore.superUser?.data ?? {
      avatarUrl: "",
      email: null,
    };

    const moduleVisits: { [key: string]: Timestamp } = {};
    moduleUidsSeen.forEach((moduleUidSeen) => {
      moduleVisits[moduleUidSeen] = serverTimestamp() as Timestamp;
    });

    // Create a user
    const userData: Omit<UserModelData, "uid"> = {
      ts: serverTimestamp() as Timestamp,
      avatarUrl,
      moduleVisits,
      planUid: this.uid,
      lastActivity: serverTimestamp() as Timestamp,
      authedUsers: [this.stores.sessionStore.uid],
      displayName: displayName.trim(),
      email,
      tokens: [],
      businessPlanUid: "",
      profile: null,
    };

    // Add to waiting list or add at once
    if (!isGhost && this.data.joinOptions.needToApply) {
      return runInAction(async () => {
        const user = await this.api.usersApi.add(userData);
        return this.addUserToApprovalList(user.uid);
      });
    }

    return runInAction(() => this.addUserToPlan(userData, isGhost));
  }

  @computed get isWaitingForApproval(): boolean {
    if (!this.data.joinOptions.needToApply) {
      return false;
    }

    return !!this.userWaitingForApproval;
  }

  @action
  async addUserToApprovalList(userUid: string) {
    // Add user to approval list
    if (!this.data.joinOptions.waitingForApproval) {
      this.data.joinOptions.waitingForApproval = [];
    }

    this.data.joinOptions.waitingForApproval.push(userUid);
    await this.api.plansApi.set(this);

    log.dev("User added to approval list", this.uid, { userUid });
  }

  @action async removeUserFromApprovalList() {
    const waitingWith = this.userWaitingForApproval;

    if (waitingWith) {
      if (this.data.joinOptions.waitingForApproval) {
        this.data.joinOptions.waitingForApproval =
          this.data.joinOptions.waitingForApproval.filter(
            (u) => u !== waitingWith.uid,
          );
      }

      await this.api.plansApi.set(this);
    }
  }

  @action async acceptUser(uid: string) {
    const userToJoin = this.stores.usersStore.byId.get(uid);

    if (this.data.joinOptions.waitingForApproval && userToJoin) {
      await this.addUserIdToPlan(uid);

      this.data.joinOptions.waitingForApproval =
        this.data.joinOptions.waitingForApproval.filter((u) => u !== uid);

      await this.api.plansApi.set(this);
    }
  }

  @action async denyUser(uid: string) {
    if (this.data.joinOptions.waitingForApproval) {
      this.data.joinOptions.waitingForApproval =
        this.data.joinOptions.waitingForApproval.filter((u) => u !== uid);
      await this.api.plansApi.set(this);
    }
  }

  @action async addUserIdToPlan(userId: string) {
    if (!this.data.userIds.includes(userId)) {
      let userToJoin = this.stores.usersStore.byId.get(userId);

      if (!userToJoin) {
        // Try to load it first
        userToJoin = await this.api.usersApi.getById(userId);

        if (!userToJoin) {
          throw "User not found to join plan";
        }
      }

      this.data.userIds.push(userId);
      await this.api.plansApi.update(this.uid, { userIds: this.data.userIds });

      if (this.data.businessPlanUid) {
        await userToJoin.setBusinessPlanUid(this.data.businessPlanUid);
        await userToJoin.setPlanUid(this.data.uid);
        if (!this.businessPlan) {
          await this.api.businessPlansApi.getById(this.data.businessPlanUid);
        }
        this.businessPlan?.addUserToPlanira(this, userId);
      }
    }
  }

  @action
  async addUserToPlan(userData: Omit<UserModelData, "uid">, isGhost?: boolean) {
    if (
      this.users.some((user) => user.data.displayName === userData.displayName)
    ) {
      throw new Error("User already exists");
    }

    const user = await this.api.usersApi.add(userData);

    // Add user to plan
    if (!isGhost) {
      this.data.userIds.push(user.uid);
    } else {
      this.data.ghostUsers.push(user.uid);
      log.dev("Ghost user created!");
    }

    // Remove user from waiting approval list if found
    if (this.data.joinOptions.waitingForApproval) {
      this.data.joinOptions.waitingForApproval =
        this.data.joinOptions.waitingForApproval.filter((u) => u !== user.uid);
    }

    if (this.data.userIds.length > 5) {
      this.trackProgress(PlanModelStep.hasMoreThanFiveUsers);
    } else if (this.data.userIds.length > 1) {
      this.trackProgress(PlanModelStep.hasMultipleUsers);
    }

    this.api.plansApi.set(this);

    log.dev("User added to plan ", this.uid, { user });

    return user;
  }

  @action
  async moveModule(moduleUid: string, toSectionUid: string) {
    const module = this.stores.modulesStore.byId.get(moduleUid);
    if (module) {
      const sectionFrom = module.section;
      const sectionTo = this.stores.sectionsStore.byId.get(toSectionUid);

      if (sectionTo) {
        await sectionFrom.removeModule(moduleUid);
        await sectionTo.addExistingModule(moduleUid);
        module.data.sectionUid = toSectionUid;
        await this.api.modulesApi.update(moduleUid, {
          sectionUid: toSectionUid,
        });
        await module.postActivityEvent(
          ActivityEventType.Moved,
          TKey.ActivityMovedCard,
          {
            oldValue: sectionFrom.data.title ?? "",
            newValue: sectionTo.data.title ?? "",
          },
        );
      }
    }
  }

  @action
  async convertQuickstartToPlanira(template: PlanTemplateType) {
    if (this.data.type === PlanType.Quickstart) {
      // Set type
      this.data.type = PlanType.Planira;
      this.data.template = template;

      // TODO - need to help rename it?

      // TODO - add activity

      await this.api.plansApi.set(this);

      trackEvent("quickstart_converted");
      showConfetti();
    }
  }

  @computed get isAllQuickstartModulesVotingStateAndDone(): boolean {
    // Only checks in first section BE AWARE

    return this.sections[1].modules.every((m) => {
      if (m.data.doneType !== ModuleDoneType.Voting) {
        return false;
      }

      return m.isDone && !m.data.completeAction;
    });
  }

  @computed get isAllQuickstartModulesDone(): boolean {
    // Only checks in first section BE AWARE
    return this.sections[1].modules.every((m) => {
      if (m.data.doneType !== ModuleDoneType.Voting) {
        return true;
      }

      return m.isDone && !m.data.completeAction;
    });
  }

  @computed get isShowingCustomStepVisible(): boolean {
    // Only checks in first section BE AWARE
    return !!this.sections[1]?.modules.find((m) => {
      if (m.data.doneType !== ModuleDoneType.Voting) {
        return false;
      }

      return m.isDone && !!m.data.completeAction;
    });
  }

  @computed get firstModuleUidNotDone(): string | undefined {
    // Only checks in first section BE AWARE
    return this.sections[1]?.modules.find((m) => !m.isDone)?.uid;
  }

  @action
  async archiveUser(userUid: string) {
    const user = this.stores.usersStore.byId.get(userUid);

    if (user) {
      user.data.archived = true;
      user.data.authedUsers = [];
      await this.api.usersApi.set(user);

      this.data.archive.userIds.push(userUid);
      this.data.userIds = this.data.userIds.filter((uid) => uid !== userUid);
      await this.api.plansApi.update(this.uid, {
        archive: this.data.archive,
        userIds: this.data.userIds,
      });
    }
  }

  @action
  async unArchiveUser(userUid: string) {
    const user = this.stores.usersStore.byId.get(userUid);

    if (user) {
      user.data.archived = false;
      await this.api.usersApi.set(user);

      this.data.archive.userIds = this.data.archive.userIds.filter(
        (uid) => uid !== userUid,
      );
      this.data.userIds.push(userUid);

      await this.api.plansApi.update(this.uid, {
        archive: this.data.archive,
        userIds: this.data.userIds,
      });
    }
  }

  @action
  async archiveSection(uid: string) {
    const s = this.stores.sectionsStore.byId.get(uid);

    if (s && !s.data.moduleIds.length) {
      // Remove from plan
      await this.removeSection(s.uid);

      // Add to archived list in plan
      this.data.archive.sectionIds.push(s.uid);
      await this.api.plansApi.update(this.uid, { archive: this.data.archive });

      // Set flag on section
      await s.setAsArchived();
    }
  }

  @action
  async unArchiveSection(uid: string) {
    const s = this.stores.sectionsStore.byId.get(uid);

    if (s && !s.data.moduleIds.length) {
      // Add from plan
      await this.addExistingSection(s.uid);

      // Remove from archived list in plan
      this.data.archive.sectionIds = this.data.archive.sectionIds.filter(
        (sUid) => sUid !== s.uid,
      );
      await this.api.plansApi.update(this.uid, { archive: this.data.archive });

      // Set flag on section
      await s.setAsArchived(false);
    }
  }

  @action
  async archiveModule(moduleUid: string) {
    const m = this.stores.modulesStore.byId.get(moduleUid);

    if (m) {
      // Remove from section
      await m.section.removeModule(m.uid);

      // Add to archived list in plan
      this.data.archive.moduleIds.push(m.uid);
      await this.api.plansApi.update(this.uid, { archive: this.data.archive });

      // Set flag on module
      await m.setAsArchived();

      // Add activity event
      m.postActivityEvent(
        ActivityEventType.Archived,
        TKey.ActivityArchivedCard,
        {},
      );

      void this.addPlaniraLogItem(
        PlaniraLogType.CardIsDone,
        `Card "${m.data.title}" archived`,
      );
    }
  }

  @action
  async unArchiveModule(moduleUid: string) {
    const m = this.stores.modulesStore.byId.get(moduleUid);

    if (m) {
      const sectionExists = !m.section.data.archived;

      if (!sectionExists) {
        // Unarchive section first
        await m.section.plan.unArchiveSection(m.section.uid);
      }

      await m.section.addExistingModule(m.uid);

      // Remove to archived list in plan
      this.data.archive.moduleIds = this.data.archive.moduleIds.filter(
        (mUid) => mUid !== m.uid,
      );
      await this.api.plansApi.update(this.uid, { archive: this.data.archive });

      // Set flag on module
      await m.setAsArchived(false);

      // Add activity event
      m.postActivityEvent(
        ActivityEventType.Archived,
        TKey.ActivityRestoredCard,
        {},
      );
    }
  }

  @computed get userWaitingForApproval(): UserModel | undefined {
    const uid = this.stores.sessionStore.uid;
    for (const user of this.usersWaitingForApproval) {
      if (user.data.authedUsers.includes(uid)) {
        return user;
      }
    }

    return undefined;
  }

  @computed get userInPlan(): UserModel | undefined {
    const uid = this.stores.sessionStore.uid;
    for (const user of this.users) {
      if (user.data.authedUsers.includes(uid)) {
        return user;
      }
    }

    for (const user of this.ghosts) {
      if (user.data.authedUsers.includes(uid)) {
        return user;
      }
    }

    return undefined;
  }

  @computed get usersWaitingForApproval(): UserModel[] {
    const u: UserModel[] = [];
    for (const userId of this.data.joinOptions?.waitingForApproval ?? []) {
      const uModel = this.stores.usersStore.byId.get(userId);
      if (uModel) {
        u.push(uModel);
      }
    }
    return u.sort((a, b) =>
      a.data.displayName.localeCompare(b.data.displayName),
    );
  }

  @computed get users(): UserModel[] {
    const u: UserModel[] = [];
    for (const userId of this.data.userIds) {
      const uModel = this.stores.usersStore.byId.get(userId);
      if (uModel) {
        u.push(uModel);
      }
    }
    return u.sort((a, b) =>
      a.data.displayName.localeCompare(b.data.displayName),
    );
  }

  @computed get usersCount(): number {
    return this.data.userIds.length ?? 0;
  }

  @computed get ghosts(): UserModel[] {
    const u: UserModel[] = [];

    for (const userId of this.data.ghostUsers) {
      const uModel = this.stores.usersStore.byId.get(userId);
      if (uModel) {
        u.push(uModel);
      }
    }
    return u;
  }

  @computed get sections(): SectionModel[] {
    const u: SectionModel[] = [];
    for (const uid of this.data.sectionIds) {
      const uModel = this.stores.sectionsStore.byId.get(uid);
      if (uModel) {
        u.push(uModel);
      }
    }
    return u;
  }

  @computed get modules(): BaseModuleModel<
    Snapshot,
    ModuleModelData<Snapshot>
  >[] {
    const u: BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>[] = [];
    for (const section of this.sections) {
      u.push(...section.modules);
    }
    return u;
  }

  @computed get moduleCount(): number {
    let c = 0;

    for (const s of this.sections) {
      c += s.data.moduleIds.length;
    }

    return c;
  }

  @computed get lastUpdatedAt(): Timestamp | undefined {
    const sortedByTs = this.modules
      .slice()
      .sort((m1, m2) =>
        m1.data.lastActivity.seconds < m2.data.lastActivity.seconds ? 1 : -1,
      );
    if (sortedByTs.length) {
      return sortedByTs[0].data.lastActivity;
    }

    return undefined;
  }

  @computed get modulesWithNewActivity(): BaseModuleModel<
    Snapshot,
    ModuleModelData<Snapshot>
  >[] {
    const user = this.userInPlan;
    if (!user) {
      return [];
    }
    return this.modules.filter((m) => m.hasNewActivity);
  }

  @computed get autoMoveDoneSection(): SectionModel | undefined {
    const doneSection = this.sections.find(
      (section) => section.data.attractState === ModuleState.Done,
    );

    return doneSection;
  }

  @computed get title() {
    const planInfo = this.modules.find(
      (module) => module.data.type === ModuleType.PlanInfo,
    );

    return planInfo?.data.title;
  }

  @computed get isEditedInSomeWay(): boolean {
    return this.hasCoverPhotoOrBackgroundColor || this.hasDescription;
  }

  @computed get hasDescription(): boolean {
    return (
      !!this.planInfo?.data.descriptionYooptaEditor &&
      !!Object.keys(this.planInfo.data.descriptionYooptaEditor).length
    );
  }

  @computed get planInfo(): PlanInfoModuleModel {
    return this.modules.find(
      (module) => module.data.type === ModuleType.PlanInfo,
    ) as PlanInfoModuleModel;
  }

  @computed get businessPlan(): BusinessPlanModel | undefined {
    return this.data.businessPlanUid
      ? this.stores.businessPlansStore.byId.get(this.data.businessPlanUid)
      : undefined;
  }

  @computed get businessStaticUsers(): BusinessStaticUserInfoWithUserUid[] {
    if (!this.businessPlan) return [];

    const users: BusinessStaticUserInfoWithUserUid[] = [];

    this.data.userIds.forEach((userUid) => {
      const info = this.businessPlan?.data.userInfo[userUid];
      if (info) {
        users.push({ userUid, ...info });
      }
    });

    return users;
  }

  @computed get hasCoverPhotoOrBackgroundColor(): boolean {
    return (
      !!this.planInfo?.data?.coverUrl ||
      this.planInfo?.data?.backgroundColor !== null
    );
  }

  @computed get hasRegistredUsers(): boolean {
    return this.users.some((user) => !!user.data.email);
  }

  toFireStore(): Omit<PlanModelData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...this.data,
    };
  }
}
